import { useEffect, useRef, useState } from 'react';

/**
 * Retorna `true` só depois de `delayMs` com `active === true`. Se `minDurationMs`
 * for &gt; 0, uma vez visível permanece por pelo menos essa duração mesmo que
 * `active` volte pra `false` (evita flicker de "apareceu e sumiu rápido").
 *
 * <p>Serve pros indicadores de espera do loading system (REDLINE-loading.md §2:
 * "espera &lt; 200ms → nada"). Um spinner ou skeleton que pisca por 80ms é pior
 * que espera silenciosa — e o inverso vale também: se o skeleton apareceu e
 * some 50ms depois porque o dado chegou, é o mesmo flicker no outro sentido.
 *
 * <p>Ciclo típico de um caller (delayMs=200, minDurationMs=400):
 * <ul>
 *   <li>t=0..200ms: nada visível (grace period).
 *   <li>t=200ms: fica visível.
 *   <li>t=200..600ms: permanece visível mesmo se `active` virar false —
 *   duração mínima na tela.
 *   <li>t=600ms+: some assim que `active` virar false.
 * </ul>
 *
 * <p>Se `active` volta pra `false` DURANTE o grace period (antes de mostrar),
 * o timer é cancelado e o hook nunca retorna true — sem indicador.
 *
 * <p>Uso típico:
 * ```jsx
 * const showSkeleton = useShowAfter(200, isLoading, 400);
 * return showSkeleton ? <Skeleton /> : null;
 * ```
 */
export default function useShowAfter(delayMs = 200, active = true, minDurationMs = 0) {
	const [show, setShow] = useState(false);
	const shownAtRef = useRef(null);

	useEffect(() => {
		// Nasce active: começa contagem pro grace period. Se ativar antes do
		// timer disparar, cancela e mantém invisível.
		if (active) {
			const id = setTimeout(() => {
				setShow(true);
				shownAtRef.current = performance.now();
			}, delayMs);
			return () => clearTimeout(id);
		}

		// Não está ativo. Se nunca ficou visível, apenas garante `show=false`.
		if (shownAtRef.current === null) {
			setShow(false);
			return undefined;
		}

		// Já ficou visível: respeita duração mínima antes de esconder.
		const elapsed = performance.now() - shownAtRef.current;
		const remaining = minDurationMs - elapsed;
		if (remaining <= 0) {
			setShow(false);
			shownAtRef.current = null;
			return undefined;
		}
		const id = setTimeout(() => {
			setShow(false);
			shownAtRef.current = null;
		}, remaining);
		return () => clearTimeout(id);
	}, [delayMs, active, minDurationMs]);

	return show;
}
